import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { IVolunteerActivity, isVolunteerBoardDisabledError } from './models';

const EVENT_VOLUNTEER_COMPANION_KEY = '/volunteering/events/companion';

const isCompanionUnavailable = (error: unknown): boolean =>
	(error as Api.IOperationResultNoValue | undefined)?.systemCode === 404 || isVolunteerBoardDisabledError(error);

export const useEnsureEventVolunteerCompanionQuery = ({
	eventSurveyId,
	enabled = true,
	impersonationContext,
}: {
	eventSurveyId?: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!eventSurveyId,
		staleTime: Number.POSITIVE_INFINITY,
		queryKey: [EVENT_VOLUNTEER_COMPANION_KEY, eventSurveyId, impersonationContext?.account?.id],
		queryFn: async (): Promise<IVolunteerActivity | null> => {
			try {
				const value = await userSession.webServiceHelper.callAsync<IVolunteerActivity>(
					Api.ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `volunteering/events/${eventSurveyId}/ensureCompanion`,
					}),
					'POST'
				);
				return value ?? null;
			} catch (error) {
				if (isCompanionUnavailable(error)) {
					return null;
				}
				throw error;
			}
		},
		meta: {
			toastErrorMessage: 'Failed to set up volunteer signups for this event',
		},
	});
};

export const invalidateEventVolunteerCompanion = (eventSurveyId?: string) =>
	reactQueryClient.invalidateQueries({
		queryKey: eventSurveyId ? [EVENT_VOLUNTEER_COMPANION_KEY, eventSurveyId] : [EVENT_VOLUNTEER_COMPANION_KEY],
	});
